import type { Locale } from "../locale.js";
import type { Phrase } from "../translate.js";

/**
 * How a namespace of messages is written down.
 *
 * The two languages sit **side by side on the same line** rather than in two
 * parallel files:
 *
 *     title: { ru: "Списки", en: "Lists" },
 *
 * That is the whole design decision. Parallel `ru.ts` / `en.ts` files drift —
 * a key gets added to one and forgotten in the other, and nobody notices until
 * an English screen shows a Russian word. Here the type says an entry has both
 * languages, so forgetting one does not compile, and a reviewer reads the pair
 * without holding two files open.
 *
 * Namespaces are separate files so that work on two screens does not land in
 * the same place twice.
 */
export type Entry = Record<Locale, Phrase>;

export type Catalogue = Record<string, Entry>;

/**
 * Prefix a table of messages with its namespace.
 *
 * `defineMessages("lists", { title: … })` gives `{ "lists.title": … }`, typed
 * so that `t("lists.title")` is checked and `t("lists.titel")` is not.
 */
export function defineMessages<P extends string, T extends Record<string, Entry>>(
  prefix: P,
  table: T,
): { [K in keyof T & string as `${P}.${K}`]: T[K] } {
  const out: Catalogue = {};
  for (const [key, entry] of Object.entries(table)) {
    out[`${prefix}.${key}`] = entry;
  }
  return out as { [K in keyof T & string as `${P}.${K}`]: T[K] };
}

/**
 * Put the namespaces together, refusing to let one shadow another.
 *
 * A plain `{ ...a, ...b }` would drop a repeated key without a word, and the
 * screen using it would quietly start showing the other namespace's wording.
 * Prefixes make a clash unlikely; this makes it impossible to ship one.
 */
export function mergeCatalogue(parts: Catalogue[]): Catalogue {
  const merged: Catalogue = {};

  for (const part of parts) {
    for (const [key, entry] of Object.entries(part)) {
      if (key in merged) {
        throw new Error(`Duplicate message key: ${key}`);
      }
      merged[key] = entry;
    }
  }

  return merged;
}
